import React, { useCallback, useState } from "react";
import {
  View,
  Platform,
  useWindowDimensions,
  LayoutChangeEvent,
} from "react-native";
import {
  FaqList,
  MediaAsset,
  ROffer,
  RunnerTip,
  RaceAttachment,
  DemandVideoAsset,
  RunnerAttachment,
  RBettingTile,
} from "./components";
import { Attachment } from "../../types/attachment";
import VideoAsset from "./components/VideoAsset";
import YouTubeVideoAsset from "./YouTubeVideoAsset";
import RText from "../RText";
import RBettingTileList from "./components/RBettingTileList";
import useDeviceType from "../../hooks/useDeviceType";
import HeroAttachment from "./components/HeroAttachment";
import RacesListAttachment from "./components/RacesListAttachment";
import ResultsListAttachment from "./components/ResultsListAttachment";
import RacecardAndResultWidget from "./components/RacecardAndResultWidget/RacecardAndResultWidget";
import { useContainerWidth } from "../../hooks/useContainerWidth";
import RacecardAndResultWidgetMultiDay from "./components/RacecardAndResultWidget/RacecardAndResultWidgetMultiDay";
import RacecardAndResultWidgetSingleDay from "./components/RacecardAndResultWidget/RacecardAndResultWidgetSingleDay";

type Props = {
  type: Attachment;
  id: string;
  caption?: string;
  metadata?: any;
  options?: string;
  href?: string;
};

export const RAttachment = ({
  type,
  id,
  caption,
  metadata,
  options,
  href,
}: Props) => {
  const { deviceType } = useDeviceType();
  const isAndroid = Platform.OS === "android";
  const isIOS = Platform.OS === "ios";
  const isMobile = isAndroid || isIOS;
  const isTabletNative = deviceType === "TABLET" && isMobile;

  const {
    width: containerWidth,
    onLayout: onContainerLayout,
    ref,
  } = useContainerWidth();

  if (metadata) {
    try {
      metadata = JSON.parse(metadata);
    } catch (e) {
      console.error("** RAttachment: error parsing metadata:", e);
    }
  }

  if (options) {
    try {
      options = JSON.parse(options);
    } catch (e) {
      console.error("** RAttachment: error parsing options:", e);
    }
  }

  const Wrapper = useCallback(
    ({ children }) => {
      const { height, width } = useWindowDimensions();
      return (
        <View
          style={{
            width: Platform.OS == "android" ? width - 20 : "100%",
          }}
        >
          {children}
        </View>
      );
    },
    [type, id, caption, metadata, options, href]
  );

  const Attachment = () => {
    let typeToCheck = metadata?.asset_type || type;
    if (typeToCheck === "racing/racecard_and_result_widget_multi_day") {
      return (
        <View style={{ minWidth: "100%" }}>
          <RacecardAndResultWidgetMultiDay id={id} />
        </View>
      );
    }
    if (typeToCheck === "site/hero") return <HeroAttachment id={id} />;
    if (typeToCheck === "racing/racecards_list")
      return <RacesListAttachment trackSlug={metadata?.track_slug} id={id} />;
    if (typeToCheck === "racing/results_list")
      return <ResultsListAttachment trackSlug={metadata?.track_slug} id={id} />;
    if (typeToCheck === "site/faq_list")
      return <FaqList isAttachment id={id} />;
    if (typeToCheck === "blob") {
      if (metadata?.content_type == "video")
        return <VideoAsset metadata={metadata} />;
      return (
        <MediaAsset id={id} metadata={metadata} caption={caption} href={href} />
      );
    }
    if (typeToCheck === "betting/betting_tile_list")
      return <RBettingTileList id={id} isAttachment />;
    if (typeToCheck === "betting/bookmaker_offer")
      return (
        <>
          {isTabletNative && (
            <View
              style={{
                width: "100%",
                height: 15,
              }}
            >
              <RText> </RText>
            </View>
          )}
          <ROffer id={id} key={id} />

          {/* this is strange, but Gaps aren't applying on ios. The only
              thing that creates a gap is a View with some content in it.
          */}
          {isTabletNative && (
            <View
              style={{
                width: "100%",
                height: 15,
              }}
            >
              <RText> </RText>
            </View>
          )}
        </>
      );
    if (typeToCheck === "betting/betting_tile_item")
      return (
        <>
          {isTabletNative && (
            <View
              style={{
                width: "100%",
                height: 15,
              }}
            >
              <RText> </RText>
            </View>
          )}
          <RBettingTile id={id} key={id} />
          {/* this is strange, but Gaps aren't applying on ios. The only
              thing that creates a gap is a View with some content in it.
          */}
          {isTabletNative && (
            <View
              style={{
                width: "100%",
                height: 15,
              }}
            >
              <RText> </RText>
            </View>
          )}
        </>
      );
    if (typeToCheck === "betting/runner_tip") return <RunnerTip id={id} />;
    if (typeToCheck === "racing/race") {
      return <RaceAttachment id={id} options={options} />;
    }
    if (typeToCheck === "racing/runner") return <RunnerAttachment id={id} />;
    if (typeToCheck === "simplestream_demand_video")
      return <DemandVideoAsset id={id} metadata={metadata} />;
    if (typeToCheck === "you_tube_video")
      return <YouTubeVideoAsset id={id} metadata={metadata} />;
    if (typeToCheck === "racing/racecard_and_result_widget_single_race")
      return (
        <RacecardAndResultWidget containerWidth={containerWidth} id={id} />
      );
    if (typeToCheck === "racing/racecard_and_result_widget_single_day")
      return <RacecardAndResultWidgetSingleDay id={id} />;
  };

  if (Platform.OS === "android" || Platform.OS === "ios")
    return (
      <Wrapper>
        <Attachment />
      </Wrapper>
    );
  return (
    <View
      ref={ref}
      onLayout={Platform.OS !== "web" ? onContainerLayout : undefined}
      style={{ width: "100%" }}
    >
      <Attachment />
    </View>
  );
};
